import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Button,
    Card,
    DatePicker,
    Dropdown,
    Empty,
    Radio,
    Select,
    Skeleton,
    Space,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {
    CloseOutlined,
    InfoCircleOutlined,
    LineChartOutlined,
    PlusOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useAuth } from "../../hooks/useAuth";
import { useCustomerSegmentationEnabled } from "../../hooks/useCustomerSegmentationEnabled";
import {
    useCreateSavedSegmentMutation,
    useGetActiveSegmentationFieldsQuery,
    useGetSavedSegmentsQuery,
    useGetSegmentCriteriaOptionsQuery,
    useSegmentComparisonTimeseriesMutation,
} from "../../services/nestjs-base-api";
import {
    ICreateSavedSegmentDto,
    ISavedSegment,
    ISegmentComparisonRequest,
    ISegmentComparisonResponse,
    ISegmentComparisonSpec,
} from "../../services/nestjs-base-api";
import { SegmentEditorModal } from "../customers/SegmentEditorModal";

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

interface Props {
    brandHash: string;
    brandName: string;
    maxSeries: number;
}

const BASELINE_ID = "baseline";
const BASELINE_COLOUR = "#1677ff";

// The amber-ring low-confidence threshold comes from the API response
// (`lowConfidenceThreshold`) — sourced from system settings and scaled to the
// chart's bucket width server-side, matching the Overview chart exactly.
// Fallback only guards against an older API shape.
const LOW_CONFIDENCE_THRESHOLD_FALLBACK = 50;

interface PersistedState {
    primaryFromIso?: string;
    primaryToIso?: string;
    compareKind?: "none" | "previous" | "yoy" | "custom";
    customCompareFromIso?: string;
    customCompareToIso?: string;
    segmentIds?: string[]; // e.g. ["baseline", "saved-12", "saved-7"]
    metric?: string;
}

// Default metric matches Overview — the "Brand Equity" latent element.
const DEFAULT_METRIC = "Brand Equity";

const persistenceKey = (userId: number | undefined, brandHash: string) =>
    userId ? `segment-comparison-state-${userId}-${brandHash}` : null;

const readPersisted = (key: string | null): PersistedState | null => {
    if (!key) return null;
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed === "object" && parsed !== null ? parsed : null;
    } catch {
        return null;
    }
};

const writePersisted = (key: string | null, state: PersistedState): void => {
    if (!key) return;
    try {
        localStorage.setItem(key, JSON.stringify(state));
    } catch {
        // ignore quota / private-mode errors
    }
};

// Matches PeriodEnum used elsewhere in the app (AdminBenchmarkingPage,
// AdminBrandEquityPage): 30 / 60 / 90 / 120 / 180 / 365 days + All Time.
const buildPeriodPresets = (): { label: string; value: [Dayjs, Dayjs] }[] => {
    const today = dayjs().endOf("day");
    const days = (n: number): [Dayjs, Dayjs] => [
        today.subtract(n - 1, "day").startOf("day"),
        today,
    ];
    return [
        { label: "Last 30 Days", value: days(30) },
        { label: "Last 60 Days", value: days(60) },
        { label: "Last 90 Days", value: days(90) },
        { label: "Last 120 Days", value: days(120) },
        { label: "Last 180 Days", value: days(180) },
        { label: "Last 365 Days", value: days(365) },
    ];
};

// Derive a label for the active period: matches one of the canonical
// presets, or "Custom" when the user has picked their own range.
const matchPresetLabel = (range: [Dayjs, Dayjs]): string => {
    const presets = buildPeriodPresets();
    for (const p of presets) {
        if (
            range[0].isSame(p.value[0], "day") &&
            range[1].isSame(p.value[1], "day")
        ) {
            return p.label;
        }
    }
    return "Custom";
};

const buildBaselineSpec = (): ISegmentComparisonSpec => ({
    id: BASELINE_ID,
    label: "All responses",
    color: BASELINE_COLOUR,
    isBaseline: true,
});

const buildSegmentSpec = (segment: ISavedSegment): ISegmentComparisonSpec => ({
    id: `saved-${segment.id}`,
    label: segment.name,
    color: segment.color ?? "#722ed1",
    savedSegmentId: segment.id,
});

// Custom dot renderer — mirrors BrandAnalyticsChart's pattern. Hides dots
// at null points (so the line gaps through them) and shows an amber ring
// when the bucket's n is below the (bucket-scaled) low-confidence threshold.
const renderConfidenceDot = (
    props: any,
    color: string,
    segmentId: string,
    threshold: number,
): React.ReactElement => {
    const { cx, cy, payload, index } = props;
    const value = payload?.[segmentId];
    if (cx == null || cy == null || value == null) {
        return <g key={`empty-${segmentId}-${index}`} />;
    }
    const n: number = payload?.[`${segmentId}__n`] ?? 0;
    const isLowConfidence = n > 0 && n < threshold;
    return (
        <g key={`dot-${segmentId}-${index}`}>
            {isLowConfidence && (
                <circle
                    cx={cx}
                    cy={cy}
                    r={8}
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    opacity={0.6}
                />
            )}
            <circle
                cx={cx}
                cy={cy}
                r={4}
                fill={color}
                stroke={color}
                strokeWidth={2}
                style={{ opacity: isLowConfidence ? 0.7 : 1 }}
            />
        </g>
    );
};

export const SegmentComparisonTab: React.FC<Props> = ({ brandHash, brandName, maxSeries }) => {
    const intl = useIntl();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const jwtToken = user?.jwtToken ?? "";
    const segmentationEnabled = useCustomerSegmentationEnabled();

    // Default primary period: last 180 days — matches the Overview chart
    // default (BrandAnalyticsComponent uses '180_days' as its initial value).
    const defaultTo = useMemo(() => dayjs().endOf("day"), []);
    const defaultFrom = useMemo(() => defaultTo.subtract(179, "day").startOf("day"), [defaultTo]);

    // Per-user-per-brand persistence: period, compare-to, and active segments
    // are restored from localStorage on mount and saved on every change.
    // Segment specs are stored as IDs (saved-N or "baseline") and rehydrated
    // once the saved segments query resolves.
    const storageKey = persistenceKey(user?.id, brandHash);
    const persistedRef = useRef<PersistedState | null>(readPersisted(storageKey));

    const [primaryRange, setPrimaryRange] = useState<[Dayjs, Dayjs]>(() => {
        const p = persistedRef.current;
        if (p?.primaryFromIso && p?.primaryToIso) {
            const from = dayjs(p.primaryFromIso);
            const to = dayjs(p.primaryToIso);
            if (from.isValid() && to.isValid()) return [from, to];
        }
        return [defaultFrom, defaultTo];
    });
    const [compareKind, setCompareKind] = useState<"none" | "previous" | "yoy" | "custom">(
        persistedRef.current?.compareKind ?? "none",
    );
    const [customCompareRange, setCustomCompareRange] = useState<[Dayjs, Dayjs] | null>(() => {
        const p = persistedRef.current;
        if (p?.customCompareFromIso && p?.customCompareToIso) {
            const from = dayjs(p.customCompareFromIso);
            const to = dayjs(p.customCompareToIso);
            if (from.isValid() && to.isValid()) return [from, to];
        }
        return null;
    });
    const [activeSegments, setActiveSegments] = useState<ISegmentComparisonSpec[]>([buildBaselineSpec()]);
    const [metric, setMetric] = useState<string>(persistedRef.current?.metric ?? DEFAULT_METRIC);

    // Y-axis scale mode — matches the Overview chart's pattern. Default is
    // 'full' (-100 to +100) so the chart never misleads by zooming in on
    // a small range without explicit user action. Persisted per brand.
    const [scaleMode, setScaleMode] = useState<"full" | "auto">(() => {
        try {
            const stored = localStorage.getItem(`segment-comparison-scale-${brandHash}`);
            return stored === "auto" || stored === "full" ? stored : "full";
        } catch {
            return "full";
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(`segment-comparison-scale-${brandHash}`, scaleMode);
        } catch {
            // ignore quota / private-mode failures
        }
    }, [brandHash, scaleMode]);

    const { data: savedSegments } = useGetSavedSegmentsQuery(
        { jwtToken },
        { skip: !jwtToken },
    );
    const { data: fieldsData } = useGetActiveSegmentationFieldsQuery(
        { jwtToken },
        { skip: !jwtToken },
    );
    const { data: criteriaOptions } = useGetSegmentCriteriaOptionsQuery(
        { jwtToken },
        { skip: !jwtToken },
    );
    const [createSegment, { isLoading: creating }] = useCreateSavedSegmentMutation();
    const [runComparison, comparisonState] = useSegmentComparisonTimeseriesMutation();

    const [defineModalOpen, setDefineModalOpen] = useState(false);
    const handleDefineNewSegment = async (payload: ICreateSavedSegmentDto) => {
        try {
            const saved = await createSegment({ jwtToken, payload }).unwrap();
            setActiveSegments((prev) => [...prev, buildSegmentSpec(saved)]);
            setDefineModalOpen(false);
        } catch (err: any) {
            // The modal stays open so user can fix and retry.
            console.error("Failed to create segment", err);
        }
    };

    const seriesUsed = useMemo(
        () => activeSegments.length * (compareKind === "none" ? 1 : 2),
        [activeSegments, compareKind],
    );
    const atSeriesCap = seriesUsed >= maxSeries;

    // Rehydrate active segments once the saved segments query resolves. We
    // can't do this synchronously in useState because we need the saved
    // segment rows to rebuild colors/labels. After rehydration, if the URL
    // carries a segmentId (e.g. user clicked "Compare on Trends" from the
    // segment library), append that segment and clear the param.
    const segmentsHydratedRef = useRef(false);
    useEffect(() => {
        if (segmentsHydratedRef.current) return;
        if (!savedSegments) return;
        const urlSegmentId = searchParams.get("segmentId");
        const ids = persistedRef.current?.segmentIds;
        const byId = new Map(savedSegments.map((s) => [`saved-${s.id}`, s]));
        const byNumericId = new Map(savedSegments.map((s) => [String(s.id), s]));

        let restored: ISegmentComparisonSpec[] = [];
        if (ids && ids.length > 0) {
            for (const id of ids) {
                if (id === BASELINE_ID) {
                    restored.push(buildBaselineSpec());
                } else {
                    const saved = byId.get(id);
                    if (saved) restored.push(buildSegmentSpec(saved));
                }
            }
        }
        if (restored.length === 0) {
            restored = [buildBaselineSpec()];
        }

        // Append URL-passed segment if not already in the list.
        if (urlSegmentId) {
            const fromUrl = byNumericId.get(urlSegmentId);
            if (fromUrl && !restored.some((s) => s.savedSegmentId === fromUrl.id)) {
                restored = [...restored, buildSegmentSpec(fromUrl)];
            }
            // Strip segmentId from the URL so subsequent renders / persistence
            // don't keep re-adding it.
            const next = new URLSearchParams(searchParams);
            next.delete("segmentId");
            setSearchParams(next, { replace: true });
        }

        setActiveSegments(restored);
        segmentsHydratedRef.current = true;
    }, [savedSegments, searchParams, setSearchParams]);

    // Persist on every change. Skipped until segments are hydrated so we
    // don't immediately overwrite the persisted segmentIds with the default
    // baseline-only set.
    useEffect(() => {
        if (!segmentsHydratedRef.current) return;
        writePersisted(storageKey, {
            primaryFromIso: primaryRange[0].toISOString(),
            primaryToIso: primaryRange[1].toISOString(),
            compareKind,
            customCompareFromIso: customCompareRange?.[0].toISOString(),
            customCompareToIso: customCompareRange?.[1].toISOString(),
            segmentIds: activeSegments.map((s) => s.id),
            metric,
        });
    }, [storageKey, primaryRange, compareKind, customCompareRange, activeSegments, metric]);

    // Fetch whenever inputs change.
    useEffect(() => {
        if (!jwtToken || activeSegments.length === 0) return;
        let comparisonPeriod: ISegmentComparisonRequest["comparisonPeriod"] | undefined;
        if (compareKind === "previous" || compareKind === "yoy") {
            comparisonPeriod = { kind: compareKind };
        } else if (compareKind === "custom" && customCompareRange) {
            comparisonPeriod = {
                dateFrom: customCompareRange[0].format("YYYY-MM-DD"),
                dateTo: customCompareRange[1].format("YYYY-MM-DD"),
            };
        }
        const payload: ISegmentComparisonRequest = {
            metric,
            primaryPeriod: {
                dateFrom: primaryRange[0].format("YYYY-MM-DD"),
                dateTo: primaryRange[1].format("YYYY-MM-DD"),
            },
            comparisonPeriod,
            segments: activeSegments,
            includeConfidenceIntervals: true,
        };
        runComparison({ jwtToken, brandHash, payload });
    }, [jwtToken, brandHash, primaryRange, compareKind, customCompareRange, activeSegments, metric, runComparison]);

    const response: ISegmentComparisonResponse | undefined = comparisonState.data;

    // True only when some bucket (any series, primary or comparison) was hidden
    // because it fell below the minimum response threshold. Used to surface the
    // missing-data explanation, but only when such gaps actually exist.
    const hasAnySuppressedData = useMemo(() => {
        if (!response) return false;
        const min = response.minResponseThreshold;
        const allSeries = [...(response.primary ?? []), ...(response.comparison ?? [])];
        return allSeries.some((s) => s.buckets.some((b) => b.n > 0 && b.n < min));
    }, [response]);

    const segmentsAvailableToAdd = useMemo<ISavedSegment[]>(() => {
        const used = new Set(activeSegments.map((s) => s.savedSegmentId).filter(Boolean) as number[]);
        return (savedSegments ?? []).filter((s) => !used.has(s.id));
    }, [savedSegments, activeSegments]);

    const addSegment = (segment: ISavedSegment) => {
        if (atSeriesCap) return;
        setActiveSegments((prev) => [...prev, buildSegmentSpec(segment)]);
    };

    const addBaseline = () => {
        if (atSeriesCap) return;
        setActiveSegments((prev) =>
            prev.some((s) => s.id === BASELINE_ID) ? prev : [buildBaselineSpec(), ...prev],
        );
    };

    const baselineActive = activeSegments.some((s) => s.id === BASELINE_ID);

    const removeSegment = (id: string) => {
        // Any segment is removable, including the baseline — users can
        // re-add "All responses" from the Add segment dropdown if needed.
        setActiveSegments((prev) => prev.filter((s) => s.id !== id));
    };

    // Chart data: merge primary + comparison series into wide format. Each
    // row also carries per-segment `n` values keyed as `${segmentId}__n` so
    // the custom dot renderer can read them and apply the low-confidence
    // amber ring (matching the Overview chart's treatment).
    const chartData = useMemo(() => {
        if (!response) return [] as Array<Record<string, number | string | null>>;
        const labels: string[] = response.primary[0]?.buckets.map((b) => b.label) ?? [];
        return labels.map((label, idx) => {
            const row: Record<string, number | string | null> = { label };
            for (const series of response.primary) {
                const bucket = series.buckets[idx];
                row[series.segmentId] = bucket?.mean ?? null;
                row[`${series.segmentId}__n`] = bucket?.n ?? 0;
            }
            for (const series of response.comparison ?? []) {
                const bucket = series.buckets[idx];
                row[`${series.segmentId}__compare`] = bucket?.mean ?? null;
                row[`${series.segmentId}__compare__n`] = bucket?.n ?? 0;
            }
            return row;
        });
    }, [response]);

    const sortedSegments = activeSegments;

    // Y-axis domain — full (-100..+100) or auto-fit to data with padding.
    // Mirrors BrandAnalyticsChart's yAxisDomain calculation.
    const yAxisDomain = useMemo<[number, number]>(() => {
        if (scaleMode === "full") return [-100, 100];
        let min = Infinity;
        let max = -Infinity;
        for (const row of chartData) {
            for (const series of response?.primary ?? []) {
                const v = row[series.segmentId];
                if (typeof v === "number" && !isNaN(v)) {
                    if (v < min) min = v;
                    if (v > max) max = v;
                }
            }
            for (const series of response?.comparison ?? []) {
                const v = row[`${series.segmentId}__compare`];
                if (typeof v === "number" && !isNaN(v)) {
                    if (v < min) min = v;
                    if (v > max) max = v;
                }
            }
        }
        if (min === Infinity || max === -Infinity) return [-100, 100];
        const padding = Math.max((max - min) * 0.1, 5);
        return [
            Math.max(-100, Math.floor(min - padding)),
            Math.min(100, Math.ceil(max + padding)),
        ];
    }, [scaleMode, chartData, response]);

    return (
        <div style={{ padding: "8px 32px 32px" }}>
            {/* Filter row */}
            <Card style={{ marginBottom: 16 }} styles={{ body: { padding: "12px 16px" } }}>
                <Space wrap size="middle">
                    <Space size={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {intl.formatMessage({ id: "segmentComparison.filter.period", defaultMessage: "Period" })}
                        </Text>
                        <Tag color={matchPresetLabel(primaryRange) === "Custom" ? "default" : "blue"} style={{ fontWeight: 500 }}>
                            {matchPresetLabel(primaryRange)}
                        </Tag>
                        <RangePicker
                            value={primaryRange}
                            onChange={(range) => {
                                if (range && range[0] && range[1]) {
                                    setPrimaryRange([range[0], range[1]]);
                                }
                            }}
                            presets={buildPeriodPresets()}
                            allowClear={false}
                        />
                    </Space>
                    <Space size={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {intl.formatMessage({ id: "segmentComparison.filter.metric", defaultMessage: "Metric" })}
                        </Text>
                        <Select
                            value={metric}
                            onChange={setMetric}
                            style={{ minWidth: 220 }}
                            options={(response?.availableMetrics ?? [metric]).map((m) => ({
                                value: m,
                                label: m,
                            }))}
                            // Show whatever's selected even if the metric list is loading.
                            optionFilterProp="label"
                            showSearch
                        />
                    </Space>
                    <Space size={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {intl.formatMessage({ id: "segmentComparison.filter.compare", defaultMessage: "Compare to" })}
                        </Text>
                        <Radio.Group
                            value={compareKind}
                            onChange={(e) => setCompareKind(e.target.value as "none" | "previous" | "yoy" | "custom")}
                            optionType="button"
                            buttonStyle="solid"
                            options={[
                                { label: intl.formatMessage({ id: "segmentComparison.filter.compareNone", defaultMessage: "None" }), value: "none" },
                                { label: intl.formatMessage({ id: "segmentComparison.filter.comparePrevious", defaultMessage: "Previous" }), value: "previous" },
                                { label: intl.formatMessage({ id: "segmentComparison.filter.compareYoy", defaultMessage: "Year over year" }), value: "yoy" },
                                { label: intl.formatMessage({ id: "segmentComparison.filter.compareCustom", defaultMessage: "Custom" }), value: "custom" },
                            ]}
                        />
                        {compareKind === "custom" && (
                            <RangePicker
                                value={customCompareRange ?? undefined}
                                onChange={(range) => {
                                    if (range && range[0] && range[1]) {
                                        setCustomCompareRange([range[0], range[1]]);
                                    } else {
                                        setCustomCompareRange(null);
                                    }
                                }}
                                allowClear={false}
                                placeholder={[
                                    intl.formatMessage({ id: "segmentComparison.filter.compareCustomFrom", defaultMessage: "From" }),
                                    intl.formatMessage({ id: "segmentComparison.filter.compareCustomTo", defaultMessage: "To" }),
                                ]}
                            />
                        )}
                    </Space>
                </Space>
            </Card>

            {/* Segment chip bar */}
            <Card style={{ marginBottom: 16 }} styles={{ body: { padding: "12px 16px" } }}>
                <Space wrap size={8}>
                    <Text type="secondary" style={{ fontSize: 12, marginRight: 4 }}>
                        {intl.formatMessage({ id: "segmentComparison.chips.label", defaultMessage: "Segments" })}
                    </Text>
                    {sortedSegments.map((s) => (
                        <SegmentChip
                            key={s.id}
                            spec={s}
                            sampleSize={response?.primary.find((p) => p.segmentId === s.id)?.sampleSize}
                            onRemove={() => removeSegment(s.id)}
                            canRemove={segmentationEnabled || !s.isBaseline}
                        />
                    ))}
                    {segmentationEnabled && (
                        <>
                            <Dropdown
                                trigger={["click"]}
                                disabled={atSeriesCap}
                                menu={{
                                    items: [
                                        ...(baselineActive
                                            ? []
                                            : [{
                                                key: "baseline",
                                                label: (
                                                    <Space>
                                                        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 5, background: BASELINE_COLOUR }} />
                                                        {intl.formatMessage({ id: "segmentComparison.chips.allResponses", defaultMessage: "All responses (baseline)" })}
                                                    </Space>
                                                ),
                                                onClick: addBaseline,
                                            }]),
                                        ...(segmentsAvailableToAdd.length === 0
                                            ? []
                                            : segmentsAvailableToAdd.map((s) => ({
                                                key: String(s.id),
                                                label: (
                                                    <Space>
                                                        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 5, background: s.color ?? "#bfbfbf" }} />
                                                        {s.name}
                                                    </Space>
                                                ),
                                                onClick: () => addSegment(s),
                                            }))),
                                        { type: "divider" as const },
                                        {
                                            key: "define-new",
                                            label: (
                                                <Space>
                                                    <PlusOutlined />
                                                    {intl.formatMessage({ id: "segmentComparison.chips.defineNew", defaultMessage: "Define new segment…" })}
                                                </Space>
                                            ),
                                            onClick: () => setDefineModalOpen(true),
                                        },
                                    ],
                                }}
                            >
                                <Tooltip title={
                                    atSeriesCap
                                        ? intl.formatMessage(
                                            { id: "segmentComparison.chips.atCap", defaultMessage: "Series cap reached ({used} of {max}). Remove a segment or disable compare-to-period." },
                                            { used: seriesUsed, max: maxSeries },
                                        )
                                        : undefined
                                }>
                                    <Button icon={<PlusOutlined />} size="small" disabled={atSeriesCap}>
                                        {intl.formatMessage({ id: "segmentComparison.chips.add", defaultMessage: "Add segment" })}
                                    </Button>
                                </Tooltip>
                            </Dropdown>
                            <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                                {intl.formatMessage(
                                    { id: "segmentComparison.chips.usage", defaultMessage: "{used} of {max} series used" },
                                    { used: seriesUsed, max: maxSeries },
                                )}
                            </Text>
                        </>
                    )}
                </Space>
            </Card>

            {!segmentationEnabled && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={intl.formatMessage({
                        id: "segmentComparison.banner.segmentationComingSoon",
                        defaultMessage:
                            "Customer segmentation is rolling out soon. You'll be able to define audiences and compare them here.",
                    })}
                />
            )}

            {/* Chart */}
            <Card
                title={
                    <Space>
                        <LineChartOutlined />
                        <span>
                            {intl.formatMessage(
                                { id: "segmentComparison.chart.title", defaultMessage: "Brand equity by segment — {brand}" },
                                { brand: brandName },
                            )}
                        </span>
                    </Space>
                }
                extra={
                    <Tooltip
                        title={
                            scaleMode === "full"
                                ? intl.formatMessage({ id: "segmentComparison.chart.zoomIn", defaultMessage: "Zoom to data range" })
                                : intl.formatMessage({ id: "segmentComparison.chart.zoomOut", defaultMessage: "Show full scale (-100 to +100)" })
                        }
                    >
                        <Button
                            type="text"
                            icon={scaleMode === "full" ? <ZoomInOutlined /> : <ZoomOutOutlined />}
                            onClick={() => setScaleMode((m) => (m === "full" ? "auto" : "full"))}
                        >
                            {scaleMode === "full"
                                ? intl.formatMessage({ id: "segmentComparison.chart.autoScale", defaultMessage: "Auto Scale" })
                                : intl.formatMessage({ id: "segmentComparison.chart.fullScale", defaultMessage: "Full Scale" })}
                        </Button>
                    </Tooltip>
                }
            >
                {comparisonState.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
                {!comparisonState.isLoading && chartData.length === 0 && (
                    <Empty
                        description={intl.formatMessage({
                            id: "segmentComparison.chart.empty",
                            defaultMessage: "No data for the selected period and segments yet.",
                        })}
                    />
                )}
                {!comparisonState.isLoading && hasAnySuppressedData && (
                    <Alert
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined />}
                        style={{ marginBottom: 16, borderRadius: 8 }}
                        message={intl.formatMessage(
                            {
                                id: "segmentComparison.missingData.info",
                                defaultMessage:
                                    "Some periods aren't plotted because they had fewer than {threshold} responses — too few to calculate a reliable score.",
                            },
                            { threshold: response?.minResponseThreshold ?? 10 },
                        )}
                    />
                )}
                {!comparisonState.isLoading && chartData.length > 0 && (
                    <ResponsiveContainer width="100%" height={420}>
                        <LineChart data={chartData} margin={{ top: 16, right: 32, left: 8, bottom: 8 }}>
                            <CartesianGrid stroke="#f0f0f0" />
                            <XAxis dataKey="label" />
                            <YAxis domain={yAxisDomain} />
                            <RechartsTooltip />
                            <Legend />
                            {response?.primary.map((s) => (
                                <Line
                                    key={s.segmentId}
                                    type="monotone"
                                    dataKey={s.segmentId}
                                    name={s.label}
                                    stroke={s.color}
                                    strokeWidth={2}
                                    dot={(props: any) => renderConfidenceDot(props, s.color, s.segmentId, response?.lowConfidenceThreshold ?? LOW_CONFIDENCE_THRESHOLD_FALLBACK)}
                                    activeDot={{ r: 5 }}
                                    connectNulls={true}
                                />
                            ))}
                            {response?.comparison?.map((s) => (
                                <Line
                                    key={`${s.segmentId}__compare`}
                                    type="monotone"
                                    dataKey={`${s.segmentId}__compare`}
                                    name={`${s.label} (prior)`}
                                    stroke={s.color}
                                    strokeWidth={2}
                                    strokeDasharray="4 4"
                                    strokeOpacity={0.7}
                                    dot={false}
                                    connectNulls={true}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                )}
                <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 12, marginBottom: 0 }}>
                    {intl.formatMessage(
                        {
                            id: "segmentComparison.chart.footer",
                            defaultMessage:
                                "Points with fewer than {amber} responses show an amber ring (lower confidence). Same thresholds as the Overview chart.",
                        },
                        {
                            amber: response?.lowConfidenceThreshold ?? LOW_CONFIDENCE_THRESHOLD_FALLBACK,
                        },
                    )}
                </Paragraph>
            </Card>

            <SegmentEditorModal
                open={defineModalOpen}
                initial={null}
                fields={fieldsData ?? []}
                cohorts={criteriaOptions?.cohorts ?? []}
                countries={criteriaOptions?.countries ?? []}
                existingColours={(savedSegments ?? []).map((s) => s.color)}
                saving={creating}
                onClose={() => setDefineModalOpen(false)}
                onSubmit={(payload) => handleDefineNewSegment(payload)}
            />
        </div>
    );
};

interface ChipProps {
    spec: ISegmentComparisonSpec;
    sampleSize?: number;
    canRemove: boolean;
    onRemove: () => void;
}

const SegmentChip: React.FC<ChipProps> = ({ spec, sampleSize, canRemove, onRemove }) => {
    const tagStyle: React.CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "2px 8px",
        background: "white",
        borderRadius: 14,
    };
    return (
        <Tag
            style={{
                ...tagStyle,
                borderColor: "#d9d9d9",
            }}
        >
            <span
                style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: 5,
                    background: spec.color,
                }}
            />
            <span style={{ fontWeight: 500 }}>{spec.label}</span>
            {sampleSize != null && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                    n={sampleSize}
                </Text>
            )}
            {canRemove && (
                <CloseOutlined
                    style={{ fontSize: 10, marginLeft: 4, cursor: "pointer", color: "#bfbfbf" }}
                    onClick={onRemove}
                />
            )}
        </Tag>
    );
};

export default SegmentComparisonTab;
